import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { userCan } from "./lib/api";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Rides from "./pages/Rides";
import RideDetail from "./pages/RideDetail";
import LiveRides from "./pages/LiveRides";
import Customers from "./pages/Customers";
import CustomerDetail from "./pages/CustomerDetail";
import Drivers from "./pages/Drivers";
import DriverDetail from "./pages/DriverDetail";
import Earnings from "./pages/Earnings";
import FareRules from "./pages/FareRules";
import PromoCodes from "./pages/PromoCodes";
import Tickets from "./pages/Tickets";
import Team from "./pages/Team";
import Settings from "./pages/Settings";
import "@/App.css";

const SECTION_ORDER = [
    "dashboard", "live", "rides", "customers", "drivers",
    "earnings", "fare-rules", "promo-codes", "tickets", "team",
];

const sectionToPath = (s) => ({
    dashboard: "/dashboard",
    live: "/live",
    rides: "/rides",
    customers: "/customers",
    drivers: "/drivers",
    earnings: "/earnings",
    "fare-rules": "/fare-rules",
    "promo-codes": "/promo-codes",
    tickets: "/tickets",
    team: "/team",
}[s] || "/dashboard");

const firstAllowedPath = (user) => {
    for (const s of SECTION_ORDER) {
        if (userCan(user, s)) return sectionToPath(s);
    }
    return "/login";
};

const Protected = ({ children, section }) => {
    const { user, loading } = useAuth();
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="text-slate-400 text-sm">Loading…</div>
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;
    if (section && !userCan(user, section)) {
        return <Navigate to={firstAllowedPath(user)} replace />;
    }
    return <Layout>{children}</Layout>;
};

const HomeRedirect = () => {
    const { user, loading } = useAuth();
    if (loading) return null;
    if (!user) return <Navigate to="/login" replace />;
    return <Navigate to={firstAllowedPath(user)} replace />;
};

function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/" element={<HomeRedirect />} />
                    <Route path="/dashboard" element={<Protected section="dashboard"><Dashboard /></Protected>} />
                    <Route path="/live" element={<Protected section="live"><LiveRides /></Protected>} />
                    <Route path="/rides" element={<Protected section="rides"><Rides /></Protected>} />
                    <Route path="/rides/:id" element={<Protected section="rides"><RideDetail /></Protected>} />
                    <Route path="/customers" element={<Protected section="customers"><Customers /></Protected>} />
                    <Route path="/customers/:id" element={<Protected section="customers"><CustomerDetail /></Protected>} />
                    <Route path="/drivers" element={<Protected section="drivers"><Drivers /></Protected>} />
                    <Route path="/drivers/:id" element={<Protected section="drivers"><DriverDetail /></Protected>} />
                    <Route path="/earnings" element={<Protected section="earnings"><Earnings /></Protected>} />
                    <Route path="/fare-rules" element={<Protected section="fare-rules"><FareRules /></Protected>} />
                    <Route path="/promo-codes" element={<Protected section="promo-codes"><PromoCodes /></Protected>} />
                    <Route path="/tickets" element={<Protected section="tickets"><Tickets /></Protected>} />
                    <Route path="/team" element={<Protected section="team"><Team /></Protected>} />
                    <Route path="/settings" element={<Protected><Settings /></Protected>} />
                    <Route path="*" element={<HomeRedirect />} />
                </Routes>
            </BrowserRouter>
        </AuthProvider>
    );
}

export default App;
